import Layout from 'components/layout';

import { i18nFunct } from '@lucid/gatsby-theme-lucid';
import { graphql } from 'gatsby';
import { Box, Heading } from 'lucid-components-react';
import React from 'react';

import Footer from 'components/footer/footer';
import Navbar from 'components/navbar/navbar';
import Link from 'components/link/link';
import PreFooterCta from 'components/pre-footer-cta/pre-footer-cta';
import SignUpCta from 'components/sign-up-cta/sign-up-cta';
import {
  Drupal_FieldNodeIntegrationHubFieldIntegrationSection,
  Drupal_FieldParagraphIntegrationHubSectionFieldIntegration,
  Drupal_NodeIntegrationHub,
  Drupal_ParagraphCtaButton,
  Drupal_ParagraphIntegrationContent,
  Drupal_ParagraphIntegrationHubSection,
  Drupal_ParagraphMarketoForm,
  Drupal_ParagraphPreFooterSection
} from 'generated/graphql';
import useGetAliases from 'hooks/use-get-aliases';
import useGetPhoneNumber from 'hooks/use-get-phone-number';
import useMarketoLink from 'hooks/use-marketo-link';
import { useMenuFooter, useMenuTopNavigation } from 'hooks/use-menu';
import { Text } from 'theme/typography';
import { LucidTheme, TemplateProps, isDefined } from 'types/types';
import { getEntityFields } from 'utils/getEntityFields';
import { getProcessedValue } from 'utils/helpers';

const IntegrationHub = ({ data, pageContext, location }: TemplateProps) => {
  const { drupal, i18nData } = data;
  const node = drupal.nodeById as Drupal_NodeIntegrationHub;
  const { entityMetatags, entityTranslations } = getEntityFields(node);
  const aliases = useGetAliases();
  const { langPrefix, pageType, nid, i18nKey, hideSsr } = pageContext;

  const i18n = i18nFunct(i18nData, i18nKey);
  const linkText = i18n('mktg-ct-enterprise-section-link');

  const preFooter =
    node.fieldPreFooterSection && node.fieldPreFooterSection.entity
      ? (node.fieldPreFooterSection.entity as Drupal_ParagraphPreFooterSection)
      : null;

  const preFooterCtas =
    preFooter && preFooter.fieldCtaButton
      ? preFooter.fieldCtaButton.filter(isDefined).map((cta, index) => {
        const { entity } = cta as { entity: Drupal_ParagraphCtaButton };
        if (entity.fieldButtonText) {
          return cta.entity as Drupal_ParagraphCtaButton;
        }
        entity.fieldButtonText =
          index === 0
            ? i18n('mktg-ct-all-get-a-quote')
            : i18n('mktg-ct-all-request-a-demo');
        cta.entity = entity;
        return cta.entity;
      })
      : null;

  const navbarMenu = useMenuTopNavigation(langPrefix);
  const footerMenu = useMenuFooter(langPrefix);
  const phoneNumber = useGetPhoneNumber(langPrefix);

  const getMarketoLink = useMarketoLink({
    i18nKey,
    langPrefix
  });

  return (
    <Layout
      entityTranslations={entityTranslations}
      hideSsr={hideSsr}
      langPrefix={langPrefix}
      location={location}
      metatags={entityMetatags}
      nid={nid}
      pageType={pageType}
    >
      <Navbar menu={navbarMenu} />
      <Box className="integration-hub">
        <header
          css={(theme: LucidTheme) => [
            {
              padding: `${theme.space[6]}px ${theme.space[2]}px`,
              backgroundColor: theme.colors.coolGrey[70],
              textAlign: 'center',
              color: theme.colors.white,
              'WebkitFontSmoothing': 'antialiased',
              backgroundSize: 'cover',
              backgroundPosition: 'center center',
              backgroundImage:
                "url('https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/atlassian/hero-gray-diagram.jpg')"
            },
            !node.fieldTitle && {
              background:
                "url('https://cdn-cashy-static-assets.lucidchart.com/marketing/pages/chart/integrations-2020/integrations-hero-image.png')",
              backgroundColor: theme.colors.white,
              height: 360,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'contain',
              backgroundPosition: 'center center',
              [`@media only screen and (max-width: ${theme.breakpoints.computer})`]: {
                backgroundSize: 'cover',
                height: 280
              },
              [`@media only screen and (max-width: ${theme.breakpoints.tabletSmall})`]: {
                height: 240
              },
              [`@media only screen and (max-width: ${theme.breakpoints.mobile})`]: {
                height: 160
              }
            }
          ]}
        >
          {node.fieldTitle && (
            <>
              <Heading
                color="white"
                css={{
                  maxWidth: '38.75rem',
                  textAlign: 'center'
                }}
                mx="auto"
                my={5}
                variant="headlineXS"
              >
                {node.fieldTitle}
              </Heading>

              <Text
                css={(theme: LucidTheme) => ({
                  maxWidth: '38.75rem',
                  margin: `${theme.space[0]} auto ${theme.space[6]}px`,
                  color: theme.colors.white,
                  textAlign: 'center'
                })}
              >
                {node.fieldDescription}
              </Text>

              {node.fieldHeaderCta &&
                node.fieldHeaderCta.filter(isDefined).map((cta, i) => {
                  const { entity } = cta as {
                    entity: Drupal_ParagraphCtaButton;
                  };
                  const { entity: marketoEntity } = entity.fieldMarketo as {
                    entity: Drupal_ParagraphMarketoForm;
                  };
                  const buttonText = entity.fieldButtonText;
                  const defaultText =
                    i === 0
                      ? i18n('mktg-ct-all-get-a-quote')
                      : i18n('mktg-ct-all-request-a-demo');

                  if (entity.fieldButtonLink) {
                    return (
                      <Link
                        key={entity.fieldButtonLink}
                        asButton
                        css={(theme: LucidTheme) => ({
                          margin: `${theme.space[0]} ${theme.space[2]}px ${theme.space[4]}px`
                        })}
                        primary={!entity.fieldIsSecondary}
                        secondary={!!entity.fieldIsSecondary}
                        to={entity.fieldButtonLink}
                      >
                        {buttonText || defaultText}
                      </Link>
                    );
                  } else if (marketoEntity.fieldMarketoId) {
                    const { url, copy } = getMarketoLink(
                      marketoEntity.fieldMarketoId
                    )!;
                    return (
                      <Link
                        key={marketoEntity.fieldMarketoId}
                        asButton
                        css={(theme: LucidTheme) => ({
                          margin: `${theme.space[0]} ${theme.space[2]}px ${theme.space[4]}px`
                        })}
                        primary={!entity.fieldIsSecondary}
                        secondary={!!entity.fieldIsSecondary}
                        to={url}
                      >
                        {buttonText || copy}
                      </Link>
                    );
                  }
                })}

              {phoneNumber && (
                <Box>
                  <a
                    css={(theme: LucidTheme) => ({
                      color: theme.colors.white,
                      '&:hover': {
                        color: theme.colors.white
                      }
                    })}
                    href={phoneNumber.link}
                  >
                    {i18n('mktg-ct-all-or-call', {
                      'sales-phone-number': phoneNumber.text
                    })}
                  </a>
                </Box>
              )}
            </>
          )}
        </header>

        {!node.fieldTitle && (
          <Box
            css={(theme: LucidTheme) => ({
              textAlign: 'center',
              [`@media only screen and (max-width: ${theme.breakpoints.mobile})`]: {
                padding: `${theme.space[0]} ${theme.space[3]}px`
              }
            })}
          >
            <Heading
              css={{
                textAlign: 'center'
              }}
              my={{ default: 4, tabletSmall: 6 }}
              variant="headlineS"
            >
              {i18n('mktg-integration-hub-title')}
            </Heading>
            <Text
              css={(theme: LucidTheme) => ({
                marginBottom: theme.space[4]
              })}
            >
              {i18n('mktg-integration-hub-subtitle')}
            </Text>
            <SignUpCta css={{ margin: '0 1rem 1rem 0' }} />
            {node.fieldHeaderCta &&
              node.fieldHeaderCta.filter(isDefined).map(cta => {
                const { entity } = cta as {
                  entity: Drupal_ParagraphCtaButton;
                };
                const { entity: marketoEntity } = entity.fieldMarketo as {
                  entity: Drupal_ParagraphMarketoForm;
                };
                const buttonText = entity.fieldButtonText;
                const defaultText = i18n('mktg-integration-hub-questions');
                if (entity.fieldButtonLink) {
                  return (
                    <Link
                      key={entity.fieldButtonLink}
                      asButton
                      secondary
                      to={entity.fieldButtonLink}
                    >
                      {buttonText || defaultText}
                    </Link>
                  );
                } else if (marketoEntity.fieldMarketoId) {
                  const { url, copy } = getMarketoLink(
                    marketoEntity.fieldMarketoId
                  )!;
                  return (
                    <Link
                      key={marketoEntity.fieldMarketoId}
                      asButton
                      secondary
                      to={url}
                    >
                      {buttonText || copy}
                    </Link>
                  );
                }
              })}
            {phoneNumber && (
              <Box>
                <a
                  css={(theme: LucidTheme) => ({
                    color: theme.colors.coolGrey[60],
                    '&:hover': {
                      color: theme.colors.coolGrey[60]
                    }
                  })}
                  href={phoneNumber.link}
                >
                  {i18n('mktg-ct-all-or-call', {
                    'sales-phone-number': phoneNumber.text
                  })}
                </a>
              </Box>
            )}
          </Box>
        )}

        {(node.fieldIntegrationSection as Drupal_FieldNodeIntegrationHubFieldIntegrationSection[]).map(
          ({ entity }, i) => {
            const {
              fieldSectionTitle,
              fieldIntegration
            } = entity as Drupal_ParagraphIntegrationHubSection;

            return (
              <Box key={fieldSectionTitle}>
                <Box
                  as="section"
                  className="integration-hub__section"
                  css={(theme: LucidTheme) => ({
                    maxWidth: '90rem',
                    margin: `${theme.space[0]} auto`,
                    padding: `${theme.space[5]}px ${theme.space[11]}px`,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    [`@media only screen and (max-width: ${theme.breakpoints.computer})`]: {
                      padding: `${theme.space[0]} ${theme.space[8]}px`
                    },
                    [`@media only screen and (max-width: ${theme.breakpoints.tablet})`]: {
                      flexDirection: 'column',
                      padding: `${theme.space[0]} ${theme.space[11]}px`
                    },
                    [`@media only screen and (max-width: ${theme.breakpoints.tabletSmall})`]: {
                      padding: `${theme.space[0]} ${theme.space[4]}px`
                    }
                  })}
                >
                  {fieldSectionTitle && (
                    <Heading
                      as="h2"
                      css={{
                        textAlign: 'center',
                        flex: '0 1 100%'
                      }}
                      my={{ default: 5, mobile: 6 }}
                      variant="subheadM"
                    >
                      {fieldSectionTitle}
                    </Heading>
                  )}
                  {(fieldIntegration as Drupal_FieldParagraphIntegrationHubSectionFieldIntegration[]).map(
                    integration => {
                      const {
                        fieldTitle,
                        fieldIntegrationDesc,
                        fieldImageUrl,
                        fieldImageAlt,
                        fieldIntegrationLink
                      } = integration.entity as Drupal_ParagraphIntegrationContent;

                      return (
                        <Box
                          key={fieldTitle}
                          css={(theme: LucidTheme) => ({
                            flex: '0 1 48%',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            margin: `${theme.space[7]}px ${theme.space[0]}`,
                            [`@media only screen and (max-width: ${theme.breakpoints.tablet})`]: {
                              flex: '0 1 100%'
                            },
                            [`@media only screen and (max-width: ${theme.breakpoints.mobile})`]: {
                              flexDirection: 'column',
                              margin: `${theme.space[5]}px ${theme.space[0]} ${theme.space[4]}px`
                            }
                          })}
                        >
                          <Box
                            css={(theme: LucidTheme) => ({
                              flex: '0 1 160px',
                              alignSelf: 'center',
                              maxHeight: 75,
                              marginRight: theme.space[4],
                              textAlign: 'right',
                              [`@media only screen and (max-width: ${theme.breakpoints.mobile})`]: {
                                flex: '0 1 100%',
                                alignSelf: 'flex-start'
                              }
                            })}
                          >
                            <img
                              alt={fieldImageAlt || fieldTitle!}
                              css={(theme: LucidTheme) => ({
                                maxHeight: 75,
                                [`@media only screen and (max-width: ${theme.breakpoints.mobile})`]: {
                                  maxHeight: 50,
                                  marginBottom: theme.space[3]
                                }
                              })}
                              src={fieldImageUrl!}
                            />
                          </Box>
                          <Box
                            css={(theme: LucidTheme) => ({
                              paddingLeft: theme.space[4],
                              flex: '0 1 calc(100% - 180px)',
                              borderLeft: `1px solid ${theme.colors.coolGrey[10]}`,
                              [`@media only screen and (max-width: ${theme.breakpoints.mobile})`]: {
                                borderLeft: 0,
                                borderTop: `1px solid ${theme.colors.coolGrey[10]}`,
                                paddingTop: theme.space[4],
                                paddingLeft: theme.space[0]
                              }
                            })}
                          >
                            <Heading as="h3" m={0} variant="subheadXS">
                              {fieldTitle}
                            </Heading>
                            {fieldIntegrationDesc && (
                              <Box
                                css={(theme: LucidTheme) => ({
                                  lineHeight: theme.lineHeights.body,
                                  margin: `${theme.space[3]}px ${theme.space[0]}`,
                                  'strong, b': {
                                    fontWeight: theme.fontWeights.subheading
                                  }
                                })}
                                dangerouslySetInnerHTML={{
                                  __html: getProcessedValue(
                                    fieldIntegrationDesc
                                  )
                                }}
                              />
                            )}
                            {fieldIntegrationLink!
                              .filter(isDefined)
                              .map((link, l) => {
                                const path =
                                  !link.url!.routed ||
                                    aliases.has(link.url!.path!)
                                    ? link.url!.path
                                    : link.url!.translate!.path;

                                return (
                                  <Box
                                    key={link.title || linkText}
                                    css={{
                                      display: 'inline-block'
                                    }}
                                  >
                                    <Link hasArrow secondary to={path!}>
                                      {link.title || linkText}
                                    </Link>
                                    {l < fieldIntegrationLink!.length - 1 && (
                                      <span
                                        css={(theme: LucidTheme) => ({
                                          display: 'inline-block',
                                          margin: `${theme.space[0]} ${theme.space[2]}px`
                                        })}
                                      >
                                        {i18n('mktg-integration-hub-and')}
                                      </span>
                                    )}
                                  </Box>
                                );
                              })}
                          </Box>
                        </Box>
                      );
                    }
                  )}
                </Box>
                {i < node.fieldIntegrationSection!.length - 1 && (
                  <hr
                    css={(theme: LucidTheme) => ({
                      margin: theme.space[4],
                      border: `1px solid ${theme.colors.coolGrey[10]}`
                    })}
                  />
                )}
              </Box>
            );
          }
        )}

        {preFooter && (
          <PreFooterCta
            ctas={preFooterCtas}
            header={preFooter.fieldHeading || (phoneNumber && phoneNumber.text)}
            pitch={
              preFooter.fieldPitch || i18n('mktg-ct-integration-footer-pitch')
            }
            subheader={
              preFooter.fieldSubheader || i18n('mktg-ct-cs-cta-header')
            }
            variant="dark"
          />
        )}
      </Box>
      <Footer entityTranslations={entityTranslations} menu={footerMenu} />
    </Layout>
  );
};

export default IntegrationHub;

export const query = graphql`
  query($nid: String!, $LanguageId: Drupal_LanguageId!, $i18nKey: String!) {
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeIntegrationHub {
          ...entityFields
          fieldTitle
          fieldHeaderCta {
            entity {
              ... on Drupal_ParagraphCtaButton {
                fieldButtonText
                fieldButtonLink
                fieldIsSecondary
                fieldMarketo {
                  entity {
                    ... on Drupal_ParagraphMarketoForm {
                      fieldMarketoId
                    }
                  }
                }
              }
            }
          }
          fieldDescription
          fieldIntegrationSection {
            entity {
              ... on Drupal_ParagraphIntegrationHubSection {
                fieldSectionTitle
                fieldIntegration {
                  entity {
                    ... on Drupal_ParagraphIntegrationContent {
                      fieldTitle
                      fieldIntegrationDesc {
                        processed
                      }
                      fieldImageUrl
                      fieldImageAlt
                      fieldIntegrationLink {
                        title
                        url {
                          path
                          routed
                          translate(language: EN) {
                            path
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          fieldPreFooterSection {
            entity {
              ... on Drupal_ParagraphPreFooterSection {
                fieldHeading
                fieldSubheader
                fieldPitch
                fieldCtaButton {
                  entity {
                    ... on Drupal_ParagraphCtaButton {
                      fieldButtonText
                      fieldButtonLink
                      fieldIsSecondary
                      fieldMarketo {
                        entity {
                          ...marketo
                        }
                      }
                    }
                  }
                }
                fieldOrCallCta {
                  entity {
                    ... on Drupal_ParagraphOrCallCta {
                      fieldCallCtaPhoneNumber
                      fieldCallCtaText
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    i18nData: allFile(
      filter: {
        sourceInstanceName: { eq: "i18n" }
        name: { in: [$i18nKey, "en"] }
      }
    ) {
      edges {
        node {
          name
          childSrcJson {
            mktg_ct_all_or_call
            mktg_integration_hub_title
            mktg_integration_hub_subtitle
            mktg_integration_hub_sign_up
            mktg_integration_hub_and
            mktg_integration_hub_questions
            mktg_subfooter_cta_primary
            mktg_ct_enterprise_section_link
            mktg_ct_all_request_a_demo
            mktg_ct_all_get_a_quote
            mktg_nav_sign_up_free
            mktg_ct_cs_cta_header
            mktg_ct_integration_footer_pitch
          }
        }
      }
    }
  }
`;
